import { useEffect, useState } from "react";
import { format } from "date-fns";
import type { DateRange } from "react-day-picker";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import AirbnbDatePicker from "@/components/AirbnbDatePicker";
import { apiRequest } from "@/lib/queryClient";
import { trackEvent, getVisitorId, getSessionId } from "@/hooks/use-page-tracking";
import { HOST_PHONE_DISPLAY, HOST_PHONE_TEL, HOST_PHONE_SMS } from "@/lib/contact-info";
import { Mail } from "lucide-react";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dateRange?: DateRange;
  guests?: number;
  /** What led the guest here: "short_stay" when they tapped the min-stay
   *  notice in the calendar, "unavailable" (default) for the dates-unavailable
   *  entry points. Drives the dialog copy and is included in analytics. */
  context?: "short_stay" | "unavailable" | "backup_interest";
  /** Configured minimum nights — used for the short-stay copy so the number
   *  matches the live rule instead of being hard-coded. */
  minNights?: number;
  /** Optional pre-fill values seeded when the dialog opens. Used by the WebMCP
   *  agent tool to fill the form on the guest's behalf; undefined for humans so
   *  there is no behavior change. */
  prefillName?: string;
  prefillEmail?: string;
  prefillMessage?: string;
};

// Lead-capture escape hatch in the booking widget: guests whose dates are
// unavailable (or who just have a question) can send an inquiry instead of
// bouncing. Posts to /api/inquiries (stores the lead + emails the host), then
// drops the guest straight into the live chat with their inquiry as the first
// message — Airbnb-style — so they get an instant reply.
export default function DateInquiryDialog({ open, onOpenChange, dateRange, guests, context = "unavailable", minNights, prefillName, prefillEmail, prefillMessage }: Props) {
  const isShortStay = context === "short_stay";
  const isBackupInterest = context === "backup_interest";
  // Keep the night count driven by the configured minimum; fall back to a
  // number-free phrasing if it wasn't provided rather than hard-coding one.
  const minPhrase = minNights && minNights > 1 ? `our usual ${minNights}-night minimum` : "our usual minimum";
  const [range, setRange] = useState<{ from: Date | undefined; to: Date | undefined }>({ from: undefined, to: undefined });
  const [calOpen, setCalOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");

  // Prefill dates from the widget selection each time the dialog opens.
  useEffect(() => {
    if (!open) return;
    setRange({ from: dateRange?.from, to: dateRange?.to });
    setPhone("");
    setStatus("idle");
    setErrorMsg("");
    // Seed name/email/message from agent-provided pre-fill values (WebMCP).
    // Undefined for humans, so their normal flow is unchanged.
    if (prefillName !== undefined) setName(prefillName);
    if (prefillEmail !== undefined) setEmail(prefillEmail);
    if (prefillMessage !== undefined) setMessage(prefillMessage);
  }, [open, dateRange?.from, dateRange?.to, prefillName, prefillEmail, prefillMessage]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (status === "loading") return;
    const trimmedEmail = email.trim();
    if (!trimmedEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setStatus("error");
      setErrorMsg("Please enter a valid email address.");
      return;
    }
    const trimmedPhone = phone.trim();
    if (!trimmedPhone || !(/^\+?[\d\s\-().]+$/.test(trimmedPhone) && (trimmedPhone.match(/\d/g)?.length ?? 0) >= 7)) {
      setStatus("error");
      setErrorMsg("Please enter a valid phone number.");
      return;
    }
    if (!range.from || !range.to) {
      setStatus("error");
      setErrorMsg("Please choose your check-in and check-out dates.");
      return;
    }
    const checkIn = format(range.from, "yyyy-MM-dd");
    const checkOut = format(range.to, "yyyy-MM-dd");
    if (checkOut <= checkIn) {
      setStatus("error");
      setErrorMsg("Check-out must be after check-in.");
      return;
    }
    setStatus("loading");
    setErrorMsg("");
    const trimmedName = name.trim();
    const trimmedMessage = message.trim();
    try {
      await apiRequest("POST", "/api/inquiries", {
        checkIn,
        checkOut,
        guests,
        email: trimmedEmail,
        name: trimmedName || undefined,
        phone: trimmedPhone || undefined,
        message: trimmedMessage || undefined,
        visitorId: getVisitorId(),
        sessionId: getSessionId(),
        inquiryType: isBackupInterest ? "backup_interest" : "date_inquiry",
      });
      trackEvent("inquiry_submitted", { checkIn, checkOut, trigger: context });

      // Open the live chat with the inquiry as the guest's first message so
      // they get an instant reply while the email inquiry reaches the host.
      let chatMsg = `Hi${trimmedName ? `, this is ${trimmedName}` : ""}! I just ${isBackupInterest ? "joined the backup list" : "sent an inquiry"} for ${format(range.from, "MMM d")} – ${format(range.to, "MMM d, yyyy")}`;
      if (guests) chatMsg += ` for ${guests} ${guests === 1 ? "guest" : "guests"}`;
      chatMsg += trimmedMessage ? `. ${trimmedMessage}` : ". Are these dates available?";
      trackEvent("inquiry_chat_opened", { checkIn, checkOut });
      window.dispatchEvent(new CustomEvent("llc:open-chat", {
        detail: { message: chatMsg, email: trimmedEmail, name: trimmedName || undefined },
      }));
      onOpenChange(false);
      setStatus("idle");
    } catch (err: any) {
      setStatus("error");
      setErrorMsg(err?.message?.replace(/^\d+:\s*/, "") || "Something went wrong. Please try again.");
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl flex items-center gap-2">
            <Mail className="h-5 w-5 text-primary" />
            {isShortStay ? "Ask about a shorter stay" : isBackupInterest ? "Join the backup list" : "Inquire about these dates"}
          </DialogTitle>
          <p className="text-sm text-muted-foreground">
            {isShortStay
              ? `Asking about a stay shorter than ${minPhrase}? Send us a note — we can often accommodate.`
              : isBackupInterest
                ? "These dates have a tentative hold. Share your details and we'll contact you first if they reopen — this does not create a booking or charge you."
              : "Dates showing unavailable, or have a question? Send us a note — we can sometimes open dates or suggest close alternatives."}
          </p>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4" noValidate data-testid="form-date-inquiry">
          <div className="border border-input rounded-xl overflow-hidden">
            <div className="grid grid-cols-2 divide-x divide-input">
              <button
                type="button"
                className="p-3.5 text-left hover:bg-muted/40 transition-colors duration-150"
                onClick={() => setCalOpen(true)}
                data-testid="button-inquiry-checkin"
              >
                <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1">
                  Check-in
                </p>
                <p className={`text-sm font-semibold ${range.from ? "text-foreground" : "text-muted-foreground"}`}>
                  {range.from ? format(range.from, "MMM d, yyyy") : "Add date"}
                </p>
              </button>
              <button
                type="button"
                className="p-3.5 text-left hover:bg-muted/40 transition-colors duration-150"
                onClick={() => setCalOpen(true)}
                data-testid="button-inquiry-checkout"
              >
                <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1">
                  Check-out
                </p>
                <p className={`text-sm font-semibold ${range.to ? "text-foreground" : "text-muted-foreground"}`}>
                  {range.to ? format(range.to, "MMM d, yyyy") : "Add date"}
                </p>
              </button>
            </div>
          </div>
          <Dialog open={calOpen} onOpenChange={setCalOpen}>
            <DialogContent showCloseButton={false} className="max-w-md sm:max-w-2xl p-0 overflow-hidden flex flex-col" style={{ height: "min(640px, 90vh)" }}>
              <DialogTitle className="sr-only">Select dates</DialogTitle>
              <AirbnbDatePicker
                value={range}
                onSave={(r) => { setRange(r); setCalOpen(false); }}
                onClose={() => setCalOpen(false)}
                monthsToShow={12}
              />
            </DialogContent>
          </Dialog>
          <div>
            <Label htmlFor="inquiry-name" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Name</Label>
            <Input
              id="inquiry-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name"
              maxLength={120}
              data-testid="input-inquiry-name"
            />
          </div>
          <div>
            <Label htmlFor="inquiry-email" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Email</Label>
            <Input
              id="inquiry-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              data-testid="input-inquiry-email"
            />
          </div>
          <div>
            <Label htmlFor="inquiry-phone" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Phone <span className="normal-case text-red-500">*</span></Label>
            <Input
              id="inquiry-phone"
              type="tel"
              autoComplete="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="(555) 123-4567"
              maxLength={30}
              required
              data-testid="input-inquiry-phone"
            />
            <p className="mt-1 text-[11px] leading-snug text-muted-foreground">
              By sharing your number, you agree we may follow up about your stay by phone or text — sometimes via Samantha, our AI booking assistant.
            </p>
          </div>
          <div>
            <Label htmlFor="inquiry-message" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Message <span className="normal-case font-normal">(optional)</span></Label>
            <Textarea
              id="inquiry-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Flexible on dates? Special occasion? Let us know."
              rows={3}
              maxLength={2000}
              data-testid="input-inquiry-message"
            />
          </div>
          {status === "error" && (
            <p className="text-sm text-red-600 dark:text-red-400" role="alert" data-testid="inquiry-error">{errorMsg}</p>
          )}
          <Button type="submit" className="w-full font-semibold py-5" disabled={status === "loading"} data-testid="button-inquiry-submit">
            {status === "loading" ? "Sending…" : isBackupInterest ? "Join backup list" : "Send inquiry"}
          </Button>
          <p className="text-center text-xs text-muted-foreground">No account needed. We'll reply instantly in chat and follow up by email.</p>
          <p className="text-center text-xs text-muted-foreground" data-testid="text-inquiry-phone">
            Prefer to talk?{" "}
            <a href={HOST_PHONE_TEL} className="font-medium underline underline-offset-2 hover:text-foreground" onClick={() => trackEvent("phone_call_click", { source: "inquiry_dialog" })} data-testid="link-inquiry-call">
              Call
            </a>{" "}
            or{" "}
            <a href={HOST_PHONE_SMS} className="font-medium underline underline-offset-2 hover:text-foreground" onClick={() => trackEvent("phone_sms_click", { source: "inquiry_dialog" })} data-testid="link-inquiry-text">
              text
            </a>{" "}
            us at{" "}
            <a href={HOST_PHONE_TEL} className="font-medium underline underline-offset-2 hover:text-foreground whitespace-nowrap" onClick={() => trackEvent("phone_call_click", { source: "inquiry_dialog_number" })} data-testid="link-inquiry-phone-number">
              {HOST_PHONE_DISPLAY}
            </a>.
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
